import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { wishlistService, WishlistItem, WishlistQueryParams } from '../services/wishlistService';
import WishlistCard from '../components/wishlist/WishlistCard';
import CreateWishlistModal from '../components/wishlist/CreateWishlistModal';
import WishlistDetailModal from '../components/wishlist/WishlistDetailModal';
import './WishlistPage.css';

const WishlistPage: React.FC = () => {
    const { t, i18n } = useTranslation(['wishlist', 'common']);
    const currentLanguage = i18n.language;

    const [items, setItems] = useState<WishlistItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [sortBy, setSortBy] = useState<'votes' | 'recent'>('votes');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const limit = 20;

    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedItem, setSelectedItem] = useState<WishlistItem | null>(null);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: WishlistQueryParams = {
                page,
                limit,
                sortBy,
            };
            if (statusFilter) params.status = statusFilter;
            if (searchQuery) params.search = searchQuery;

            const result = await wishlistService.list(params);
            setItems(result.items);
            setTotal(result.total);
        } catch {
            setError(t('wishlist:loadError'));
        } finally {
            setLoading(false);
        }
    }, [page, sortBy, statusFilter, searchQuery, t]);

    const fetchMyVotes = useCallback(async () => {
        try {
            const voted = await wishlistService.getMyVotes();
            setVotedIds(new Set(voted.map((v) => v.id)));
        } catch {
            // Not critical — ignore
        }
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    useEffect(() => {
        fetchMyVotes();
    }, [fetchMyVotes]);

    const handleVote = async (itemId: string) => {
        try {
            const result = await wishlistService.toggleVote(itemId);
            setItems((prev) =>
                prev.map((item) =>
                    item.id === itemId ? { ...item, voteCount: result.voteCount } : item,
                ),
            );
            // Also update the selected item if it's the one being voted on
            setSelectedItem((prev) =>
                prev && prev.id === itemId ? { ...prev, voteCount: result.voteCount } : prev,
            );
            setVotedIds((prev) => {
                const next = new Set(prev);
                if (result.voted) {
                    next.add(itemId);
                } else {
                    next.delete(itemId);
                }
                return next;
            });
        } catch {
            // Ignore vote errors silently
        }
    };

    const handleCreate = async (data: { customTopic: string; description: string }) => {
        setIsSubmitting(true);
        try {
            await wishlistService.create({
                customTopic: data.customTopic,
                description: data.description || undefined,
            });
            setIsCreateModalOpen(false);
        } catch {
            setError(t('wishlist:createError'));
        } finally {
            setIsSubmitting(false);
            await fetchItems();
            fetchMyVotes();
        }
    };

    const handleCardClick = (item: WishlistItem) => {
        setSelectedItem(item);
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="wishlist-page">
            <div className="wishlist-page__header">
                <h1>{t('wishlist:title')}</h1>
                <p className="wishlist-page__subtitle">{t('wishlist:subtitle')}</p>
                <button
                    className="wishlist-page__create-btn"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M8 3V13M3 8H13" />
                    </svg>
                    {t('wishlist:createButton')}
                </button>
            </div>

            <div className="wishlist-page__filters">
                <div className="wishlist-page__search">
                    <input
                        type="text"
                        placeholder={t('wishlist:searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    />
                </div>
                <div className="wishlist-page__filter-group">
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    >
                        <option value="">{t('wishlist:allStatuses')}</option>
                        <option value="open">{t('wishlist:status_open')}</option>
                        <option value="in_progress">{t('wishlist:status_in_progress')}</option>
                        <option value="fulfilled">{t('wishlist:status_fulfilled')}</option>
                        <option value="declined">{t('wishlist:status_declined')}</option>
                    </select>
                    <select
                        value={sortBy}
                        onChange={(e) => { setSortBy(e.target.value as 'votes' | 'recent'); setPage(1); }}
                    >
                        <option value="votes">{t('wishlist:sortByVotes')}</option>
                        <option value="recent">{t('wishlist:sortByRecent')}</option>
                    </select>
                </div>
            </div>

            {error && <div className="wishlist-page__error">{error}</div>}

            {loading ? (
                <div className="wishlist-page__loading">{t('common:loading')}</div>
            ) : items.length === 0 ? (
                <div className="wishlist-page__empty">
                    <p>{t('wishlist:emptyState')}</p>
                </div>
            ) : (
                <>
                    <div className="wishlist-page__grid">
                        {items.map((item) => (
                            <WishlistCard
                                key={item.id}
                                item={item}
                                hasVoted={votedIds.has(item.id)}
                                onClick={handleCardClick}
                                currentLanguage={currentLanguage}
                            />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="wishlist-page__pagination">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                {t('common:previous')}
                            </button>
                            <span>{t('wishlist:pageInfo', { page, totalPages })}</span>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                {t('common:next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            <CreateWishlistModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreate}
                isSubmitting={isSubmitting}
            />

            {selectedItem && (
                <WishlistDetailModal
                    item={selectedItem}
                    hasVoted={votedIds.has(selectedItem.id)}
                    onVote={handleVote}
                    onClose={() => setSelectedItem(null)}
                    currentLanguage={currentLanguage}
                />
            )}
        </div>
    );
};

export default WishlistPage;
